'use client';

import { Box, Container, Stack, styled, Typography } from '@mui/material';
import Image from 'next/image';
import { HomepageContent } from '../../../../lib/fetch-content';
import { useExperienceCategory } from '../../hooks/use-experience-category';
import { CategoryContent, getCategoriesContent } from '../../lib/get-categories-content';
import { CategoryModal } from '../category-modal/category-modal';

interface IconProps extends CategoryContent {}

const IconImage = styled(Image)`
  position: absolute;
  top: 0;
  left: 0;
  object-fit: contain;
  height: 150px;
  width: 150px;
  transition: opacity 0.5s ease-in-out;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    height: 250px;
    width: 250px;
  }
`;

const ConstellationImage = styled(IconImage)`
  opacity: 0;
`;

const IconStack = styled(Stack)`
  background: none;
  border: none;
  padding: 0;
  position: relative;
  height: 150px;
  width: 150px;
  cursor: pointer;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    height: 250px;
    width: 250px;

    &:hover,
    &:focus {
      .icon-image {
        opacity: 0;
      }
      .constellation-image {
        opacity: 1;
      }
      .MuiTypography-root {
        text-shadow: 0 0 10px ${({ theme }) => theme.palette.common.cream};
      }
    }
  }
`;

const IconTypography = styled(Typography)`
  position: absolute;
  font-size: 18px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.common.cream};
  text-shadow: 0 0 10px rgba(0, 0, 0, 0);
  transition: text-shadow 0.5s ease-in-out;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    font-size: 30px;
  }
`;

export function Icon({
  text,
  category,
  icon,
  constellation,
  textOffset: { top, left },
}: IconProps) {
  const { setCategory } = useExperienceCategory();
  const onClick = (e: React.MouseEvent<any>) => {
    e.preventDefault();
    setCategory(category);
    // blur
    e.currentTarget.blur();
  };
  return (
    <IconStack as="button" onClick={onClick}>
      <IconImage {...icon} height={500} width={500} className="icon-image" />
      <ConstellationImage
        {...constellation}
        height={500}
        width={500}
        className="constellation-image"
      />
      <IconTypography sx={{ top: `${top}%`, left: `${left}%` }} variant="h1">
        {text}
      </IconTypography>
    </IconStack>
  );
}

interface ExperienceInnerProps {
  homepageContent: HomepageContent;
}

export function ExperienceInner({ homepageContent }: ExperienceInnerProps) {
  const categories = getCategoriesContent();
  return (
    <>
      <CategoryModal homepageContent={homepageContent} />
      <Container
        sx={{
          marginTop: {
            xs: 0,
            sm: '-80px',
          },
        }}
      >
        <Stack gap={4} direction="row" flexWrap="wrap" justifyContent="space-between">
          {categories.map((icon, index) => (
            <Box
              key={index}
              sx={{
                width: {
                  xs: '45%',
                  sm: '30%',
                },
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Icon {...icon} />
            </Box>
          ))}
        </Stack>
      </Container>
    </>
  );
}
